import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { formsApi, submissionsApi } from '@/lib/api'
import {
  ArrowLeft, RefreshCw, Trash2, Eye, X,
  CheckCircle2, Clock, AlertCircle, ChevronLeft, ChevronRight,
  FileText, User2, Calendar, Hash, MapPin, Smartphone, Printer,
} from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import type { Submission, SubmissionDetail, WorkflowEvent } from '@/types'

// ── Print handler ─────────────────────────────────────────────────────────
function handlePrint(submission: Submission, detail: SubmissionDetail | undefined) {
  const pw = window.open('', '_blank', 'width=900,height=800')
  if (!pw) return

  const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
    submitted: { label: 'Submetido', color: '#1d4ed8', bg: '#dbeafe' },
    approved:  { label: 'Aprovado',  color: '#15803d', bg: '#dcfce7' },
    rejected:  { label: 'Rejeitado', color: '#b91c1c', bg: '#fee2e2' },
    draft:     { label: 'Rascunho', color: '#6b7280', bg: '#f3f4f6' },
  }
  const st = statusLabels[submission.status] ?? statusLabels.draft

  const submittedAt = submission.submitted_at
    ? new Date(submission.submitted_at).toLocaleString('pt-PT', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—'

  // Separate media fields (photo/signature) from regular fields
  const mediaFields = (detail?.values ?? []).filter(
    v => v.field_type === 'photo' || v.field_type === 'signature'
  )
  const regularFields = (detail?.values ?? []).filter(
    v => v.field_type !== 'photo' && v.field_type !== 'signature'
  )

  const renderFieldValue = (v: { field_type: string; value: string | null }) => {
    if (v.value === null || v.value === undefined || v.value === '') return '<span style="color:#9ca3af;font-style:italic">Não preenchido</span>'
    if (v.field_type === 'boolean') return v.value === 'true'
      ? '<span style="color:#15803d;font-weight:600">✓ Sim</span>'
      : '<span style="color:#6b7280">✗ Não</span>'
    return String(v.value).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')
  }

  const regularRows = regularFields.map((v, i) => `
    <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f9fafb'}">
      <td style="padding:10px 14px;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;border-right:1px solid #e5e7eb;width:35%;vertical-align:top">
        ${v.field_label}
      </td>
      <td style="padding:10px 14px;font-size:13px;color:#111827;vertical-align:top">
        ${renderFieldValue(v)}
      </td>
    </tr>`).join('')

  const mediaHtml = mediaFields.length > 0 ? `
    <div style="margin-top:24px">
      <div style="font-size:11px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #e5e7eb">
        Fotografias &amp; Assinaturas
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:20px">
        ${mediaFields.map(v => {
          const hasImage = v.value && v.value.startsWith('data:image')
          return `
          <div style="text-align:center">
            <div style="font-size:10px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">${v.field_label}</div>
            ${hasImage
              ? `<img src="${v.value}" style="max-width:200px;max-height:160px;border:1px solid #d1d5db;border-radius:8px;object-fit:contain;background:#f9fafb;padding:4px" />`
              : `<div style="width:200px;height:100px;border:1px dashed #d1d5db;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:11px;font-style:italic">Sem imagem</div>`
            }
          </div>`
        }).join('')}
      </div>
    </div>` : ''

  // ── Workflow timeline ────────────────────────────────────────────────────
  const wf = detail?.workflow
  const workflowHtml = wf && wf.events.length > 0 ? (() => {
    const actionColors: Record<string, { icon: string; bg: string; border: string }> = {
      'Aprovado':   { icon: '✓', bg: '#f0fdf4', border: '#86efac' },
      'Rejeitado':  { icon: '✗', bg: '#fef2f2', border: '#fca5a5' },
      'Aprovada':   { icon: '✓', bg: '#f0fdf4', border: '#86efac' },
      'Rejeitada':  { icon: '✗', bg: '#fef2f2', border: '#fca5a5' },
      'Iniciado':   { icon: '▶', bg: '#eff6ff', border: '#93c5fd' },
      'Concluído':  { icon: '■', bg: '#f5f3ff', border: '#c4b5fd' },
    }
    const getStyle = (action: string | null) => {
      if (!action) return { icon: '●', bg: '#f9fafb', border: '#d1d5db' }
      for (const [key, val] of Object.entries(actionColors)) {
        if (action.toLowerCase().includes(key.toLowerCase())) return val
      }
      return { icon: '→', bg: '#f8fafc', border: '#cbd5e1' }
    }

    const eventRows = wf.events.map((ev: WorkflowEvent) => {
      const s = getStyle(ev.action_name)
      const evDate = new Date(ev.created_at).toLocaleString('pt-PT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
      const fromLabel = ev.from_state_name
        ? `<span style="color:#6b7280">${ev.from_state_name.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</span> <span style="color:#9ca3af">→</span> `
        : ''
      return `
        <tr>
          <td style="padding:10px 14px;vertical-align:top;width:120px">
            <div style="font-size:11px;font-weight:600;color:#374151">${evDate}</div>
          </td>
          <td style="padding:10px 4px;vertical-align:top;width:32px;text-align:center">
            <div style="width:24px;height:24px;border-radius:50%;background:${s.bg};border:2px solid ${s.border};display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#374151">${s.icon}</div>
          </td>
          <td style="padding:10px 14px;vertical-align:top">
            <div style="font-size:12px;font-weight:700;color:#111827;margin-bottom:2px">${(ev.action_name ?? 'Transição').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
            <div style="font-size:11px;color:#6b7280;margin-bottom:2px">${fromLabel}<span style="color:#1d4ed8;font-weight:600">${ev.to_state_name.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</span></div>
            ${ev.performed_by_name ? `<div style="font-size:11px;color:#374151">Por: <strong>${ev.performed_by_name.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</strong></div>` : ''}
            ${ev.comment ? `<div style="font-size:11px;color:#6b7280;margin-top:3px;font-style:italic;background:#f9fafb;padding:4px 8px;border-radius:4px;border-left:3px solid #d1d5db">"${ev.comment.replace(/</g,'&lt;').replace(/>/g,'&gt;')}"</div>` : ''}
          </td>
        </tr>`
    }).join('')

    const completedAt = wf.completed_at
      ? new Date(wf.completed_at).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null

    return `
    <div style="margin-top:28px;page-break-inside:avoid">
      <div style="font-size:11px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #e5e7eb">
        Histório de Fluxo — ${wf.workflow_name.replace(/</g,'&lt;').replace(/>/g,'&gt;')}
        <span style="margin-left:8px;font-size:10px;font-weight:400;color:#6b7280;text-transform:none;letter-spacing:0">
          ${wf.is_active
            ? `Estado atual: <strong style="color:#1d4ed8">${wf.current_state_name}</strong>`
            : `Concluído ${completedAt ? `em ${completedAt}` : ''}`}
        </span>
      </div>
      <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">
        <thead>
          <tr style="background:#f8fafc">
            <th style="padding:8px 14px;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;text-align:left;border-bottom:1px solid #e5e7eb">Data / Hora</th>
            <th style="padding:8px 4px;border-bottom:1px solid #e5e7eb"></th>
            <th style="padding:8px 14px;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;text-align:left;border-bottom:1px solid #e5e7eb">Ação / Estado</th>
          </tr>
        </thead>
        <tbody>${eventRows}</tbody>
      </table>
    </div>`
  })() : ''

  const deviceHtml = submission.device_model ? `
    <div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#374151">
      <span style="font-size:14px">📱</span>
      <span>${submission.device_model}${submission.device_os ? ` · ${submission.device_os}` : ''}</span>
    </div>` : ''

  pw.document.write(`<!DOCTYPE html>
<html lang="pt">
<head>
  <meta charset="UTF-8"/>
  <title>${submission.form_name} — Submissão #${submission.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, Helvetica, sans-serif;
      font-size: 13px;
      color: #111827;
      background: #fff;
    }
    .page { max-width: 800px; margin: 0 auto; padding: 32px; }

    /* Header */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 20px;
      border-bottom: 3px solid #1e40af;
      margin-bottom: 24px;
    }
    .brand { display: flex; flex-direction: column; gap: 2px; }
    .brand-name { font-size: 22px; font-weight: 800; color: #1e40af; letter-spacing: -0.5px; }
    .brand-sub  { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .08em; }
    .doc-info   { text-align: right; }
    .doc-number { font-size: 26px; font-weight: 800; color: #1e40af; }
    .doc-label  { font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: .06em; }

    /* Title bar */
    .title-bar {
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      color: #fff;
      padding: 14px 20px;
      border-radius: 10px;
      margin-bottom: 20px;
    }
    .title-bar h1 { font-size: 17px; font-weight: 700; margin-bottom: 2px; }
    .title-bar p  { font-size: 11px; opacity: .75; }

    /* Meta grid */
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 24px;
    }
    .meta-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 14px;
    }
    .meta-card .label { font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
    .meta-card .value { font-size: 13px; font-weight: 600; color: #1e293b; }
    .status-chip {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      color: ${st.color};
      background: ${st.bg};
    }

    /* Fields table */
    .section-title {
      font-size: 11px;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: .06em;
      margin-bottom: 10px;
      padding-bottom: 6px;
      border-bottom: 2px solid #e5e7eb;
    }
    .fields-table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 24px;
      font-size: 13px;
    }
    .fields-table td { border-bottom: 1px solid #e5e7eb; }
    .fields-table tr:last-child td { border-bottom: none; }

    /* Footer */
    .footer {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 10px;
      color: #9ca3af;
    }
    .signature-box {
      border-top: 1px solid #374151;
      width: 200px;
      margin-top: 40px;
      padding-top: 6px;
      font-size: 10px;
      color: #6b7280;
      text-align: center;
    }

    @media print {
      @page { size: A4; margin: 1.5cm; }
      body { background: #fff; }
      .page { padding: 0; max-width: 100%; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="page">

    <!-- Header -->
    <div class="header">
      <div class="brand">
        <div class="brand-name">ShopfloorHub</div>
        <div class="brand-sub">Plataforma de Gestão Industrial</div>
      </div>
      <div class="doc-info">
        <div class="doc-label">Submissão</div>
        <div class="doc-number">#${submission.id}</div>
        <div style="font-size:11px;color:#6b7280;margin-top:2px">${submittedAt}</div>
      </div>
    </div>

    <!-- Title bar -->
    <div class="title-bar">
      <h1>${submission.form_name}</h1>
      <p>Registo de formulário submetido através da aplicação móvel</p>
    </div>

    <!-- Meta -->
    <div class="meta-grid">
      <div class="meta-card">
        <div class="label">Submetido por</div>
        <div class="value">${submission.submitted_by_name}</div>
      </div>
      <div class="meta-card">
        <div class="label">Data / Hora</div>
        <div class="value">${submittedAt}</div>
      </div>
      <div class="meta-card">
        <div class="label">Estado</div>
        <div class="value"><span class="status-chip">${st.label}</span></div>
      </div>
      <div class="meta-card">
        <div class="label">Campos preenchidos</div>
        <div class="value">${submission.values_count}</div>
      </div>
      ${deviceHtml ? `<div class="meta-card"><div class="label">Dispositivo</div><div class="value" style="font-size:12px">${submission.device_model ?? ''}${submission.device_os ? `<br/><span style="font-weight:400;color:#6b7280">${submission.device_os}</span>` : ''}</div></div>` : ''}
      ${submission.latitude != null ? `<div class="meta-card"><div class="label">Localização GPS</div><div class="value" style="font-size:11px;font-family:monospace">${submission.latitude?.toFixed(5)}, ${submission.longitude?.toFixed(5)}${submission.gps_accuracy != null ? `<br/><span style="font-weight:400;color:#6b7280">±${Math.round(submission.gps_accuracy ?? 0)}m</span>` : ''}</div></div>` : ''}
    </div>

    <!-- Fields -->
    ${regularFields.length > 0 ? `
    <div class="section-title">Dados do Formulário</div>
    <table class="fields-table">
      <tbody>${regularRows}</tbody>
    </table>` : ''}

    <!-- Media -->
    ${mediaHtml}

    <!-- Workflow timeline -->
    ${workflowHtml}

    <!-- Footer -->
    <div class="footer">
      <div>
        <div>Gerado por ShopfloorHub · ${new Date().toLocaleString('pt-PT')}</div>
        <div style="margin-top:2px">Documento confidencial — uso interno</div>
      </div>
      <div style="text-align:right">
        <div class="signature-box">Assinatura / Aprovação</div>
      </div>
    </div>

  </div>
  <script>window.onload = function(){ window.print(); }</script>
</body>
</html>`)
  pw.document.close()
  pw.focus()
}


// ── Status badge ───────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  submitted: { label: 'Submetido', cls: 'bg-blue-50 text-blue-700', icon: <CheckCircle2 className="h-3 w-3" /> },
  approved:  { label: 'Aprovado',  cls: 'bg-green-50 text-green-700', icon: <CheckCircle2 className="h-3 w-3" /> },
  rejected:  { label: 'Rejeitado', cls: 'bg-red-50 text-red-700', icon: <AlertCircle className="h-3 w-3" /> },
  draft:     { label: 'Rascunho', cls: 'bg-gray-100 text-gray-600', icon: <Clock className="h-3 w-3" /> },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>
      {cfg.icon} {cfg.label}
    </span>
  )
}

// ── Field value renderer ───────────────────────────────────────────────────
function FieldValue({ type, value }: { type: string; value: string | null }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 italic text-sm">—</span>
  }
  if (type === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 text-sm font-medium ${value === 'true' ? 'text-green-600' : 'text-gray-500'}`}>
        {value === 'true' ? <CheckCircle2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
        {value === 'true' ? 'Sim' : 'Não'}
      </span>
    )
  }
  if (type === 'photo' || type === 'signature') {
    if (value && value.startsWith('data:image')) {
      return (
        <img
          src={value}
          alt="foto submetida"
          className="max-h-64 w-auto rounded-lg object-contain border border-gray-100 cursor-zoom-in"
          onClick={() => window.open(value)}
        />
      )
    }
    return <span className="text-xs text-gray-400 italic">Foto não disponível</span>
  }
  return <span className="text-sm text-gray-900">{value}</span>
}

// ── Detail slide-over ──────────────────────────────────────────────────────
function SubmissionDetail({ submission, onClose }: {
  submission: Submission
  onClose: () => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['submission-detail', submission.id],
    queryFn: () => submissionsApi.get(submission.id),
  })
  const detail = data?.data as SubmissionDetail | undefined

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Submissão #{submission.id}</h2>
            <p className="text-sm text-gray-500">{submission.form_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePrint(submission, detail)}
              disabled={isLoading}
              className="rounded-lg p-2 hover:bg-gray-100 text-gray-600"
              title="Imprimir"
            >
              <Printer className="h-5 w-5" />
            </button>
            <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Meta info */}
        <div className="grid grid-cols-2 gap-4 border-b border-gray-100 px-6 py-4 bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User2 className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-400">Utilizador</p>
              <p className="font-medium text-gray-900">{submission.submitted_by_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-400">Data de submissão</p>
              <p className="font-medium text-gray-900">{formatDateTime(submission.submitted_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Hash className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-400">Estado</p>
              <StatusBadge status={submission.status} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileText className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-400">Campos preenchidos</p>
              <p className="font-medium text-gray-900">{submission.values_count}</p>
            </div>
          </div>
          {/* Device info */}
          {submission.device_model && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Smartphone className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400">Dispositivo</p>
                <p className="font-medium text-gray-900">{submission.device_model}</p>
                {submission.device_os && <p className="text-xs text-gray-400">{submission.device_os}</p>}
              </div>
            </div>
          )}
          {submission.latitude != null && submission.longitude != null && (
            <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
              <MapPin className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400">Localização GPS</p>
                <a
                  href={`https://maps.google.com/?q=${submission.latitude},${submission.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary-600 hover:underline"
                >
                  {submission.latitude.toFixed(6)}, {submission.longitude.toFixed(6)}
                  {submission.gps_accuracy != null && (
                    <span className="ml-1 text-xs text-gray-400">±{Math.round(submission.gps_accuracy)}m</span>
                  )}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Values */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Field values */}
              {(detail?.values.length ?? 0) > 0 && (
                <div className="space-y-3">
                  {detail?.values.map((v) => (
                    <div key={v.field_id} className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                        {v.field_label}
                        <span className="ml-2 normal-case font-mono text-gray-300">[{v.field_type}]</span>
                      </p>
                      <FieldValue type={v.field_type} value={v.value} />
                    </div>
                  ))}
                </div>
              )}
              {(detail?.values.length ?? 0) === 0 && !detail?.workflow && (
                <p className="text-center text-sm text-gray-400 py-8">Sem respostas registadas</p>
              )}

              {/* Workflow timeline */}
              {detail?.workflow && detail.workflow.events.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">
                      Fluxo — {detail.workflow.workflow_name}
                    </h3>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                      detail.workflow.is_active ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
                    }`}>
                      {detail.workflow.is_active ? detail.workflow.current_state_name : 'Concluído'}
                    </span>
                  </div>
                  <ol className="relative border-l-2 border-gray-200 ml-2 space-y-0">
                    {detail.workflow.events.map((ev, i) => {
                      const isApprove = /aprov/i.test(ev.action_name ?? '')
                      const isReject  = /rejeit/i.test(ev.action_name ?? '')
                      const isStart   = /inici/i.test(ev.action_name ?? '')
                      const isDone    = !detail.workflow!.is_active && i === detail.workflow!.events.length - 1
                      const dotCls = isApprove || isDone
                        ? 'bg-green-500 border-green-500'
                        : isReject
                        ? 'bg-red-500 border-red-500'
                        : isStart
                        ? 'bg-blue-500 border-blue-500'
                        : 'bg-gray-400 border-gray-400'
                      return (
                        <li key={ev.id} className="ml-4 pb-5 last:pb-0">
                          <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white ${dotCls}`} />
                          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs font-bold text-gray-800">
                                  {ev.action_name ?? 'Transição'}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {ev.from_state_name && (
                                    <span className="text-gray-400">{ev.from_state_name} → </span>
                                  )}
                                  <span className="font-semibold text-gray-700">{ev.to_state_name}</span>
                                </p>
                                {ev.performed_by_name && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Por: <span className="font-medium">{ev.performed_by_name}</span>
                                  </p>
                                )}
                                {ev.comment && (
                                  <p className="mt-1 text-xs italic text-gray-500 border-l-2 border-gray-300 pl-2">
                                    "{ev.comment}"
                                  </p>
                                )}
                              </div>
                              <time className="whitespace-nowrap text-xs text-gray-400 pt-0.5">
                                {formatDateTime(ev.created_at)}
                              </time>
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ol>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Delete confirm modal ───────────────────────────────────────────────────
function DeleteModal({ sub, onClose }: { sub: Submission; onClose: () => void }) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => submissionsApi.delete(sub.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['submissions'] })
      onClose()
    },
  })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Eliminar submissão?</h2>
        <p className="mb-4 text-sm text-gray-600">
          A submissão <strong>#{sub.id}</strong> de <strong>{sub.submitted_by_name}</strong> será eliminada permanentemente.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="btn btn-danger flex-1"
          >
            {mutation.isPending ? 'A eliminar...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function FormSubmissionsPage() {
  const { id: formIdParam } = useParams<{ id: string }>()
  const formId = formIdParam ? Number(formIdParam) : undefined
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedSub, setSelectedSub] = useState<Submission | null>(null)
  const [deleteSub, setDeleteSub] = useState<Submission | null>(null)

  // Load form name when accessed from /forms/:id/submissions
  const { data: formData } = useQuery({
    queryKey: ['form', formId],
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  })
  const formName = formData?.data?.name

  // Load submissions
  const { data, isLoading, isError } = useQuery({
    queryKey: ['submissions', formId, page, statusFilter],
    queryFn: () =>
      formId
        ? formsApi.submissions(formId, { page, page_size: 20, status: statusFilter || undefined })
        : submissionsApi.listAll({ page, page_size: 20, status: statusFilter || undefined }),
  })

  const subs: Submission[] = data?.data?.items ?? []
  const total = data?.data?.total ?? 0
  const totalPages = data?.data?.total_pages ?? 1

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4">
        {formId && (
          <button onClick={() => navigate('/forms')} className="rounded-lg p-2 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {formId ? `Submissões — ${formName ?? '...'}` : 'Todas as Submissões'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {total} submissão{total !== 1 ? 'ões' : ''} encontrada{total !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['submissions'] })}
          className="btn btn-secondary"
          title="Atualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="input"
        >
          <option value="">Todos os estados</option>
          <option value="submitted">Submetido</option>
          <option value="approved">Aprovado</option>
          <option value="rejected">Rejeitado</option>
          <option value="draft">Rascunho</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-4 card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : isError ? (
          <p className="py-12 text-center text-sm text-red-500">Erro ao carregar submissões</p>
        ) : subs.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-4 text-sm text-gray-500">Nenhuma submissão encontrada</p>
            {formId && (
              <p className="mt-1 text-xs text-gray-400">
                Quando alguém preencher este formulário no telemóvel e sincronizar, aparece aqui.
              </p>
            )}
          </div>
        ) : (
          <table className="table w-full">
            <thead>
              <tr>
                <th>#</th>
                {!formId && <th>Formulário</th>}
                <th>Utilizador</th>
                <th>Estado</th>
                <th>Data</th>
                <th className="text-center">Respostas</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(sub => (
                <tr key={sub.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedSub(sub)}>
                  <td className="font-mono text-sm text-gray-500">#{sub.id}</td>
                  {!formId && (
                    <td>
                      <Link
                        to={`/forms/${sub.form_id}/submissions`}
                        className="font-medium text-primary-600 hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        {sub.form_name}
                      </Link>
                    </td>
                  )}
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                        {sub.submitted_by_name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </div>
                      <span className="text-sm text-gray-900">{sub.submitted_by_name}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={sub.status} /></td>
                  <td className="text-sm text-gray-500">{formatDateTime(sub.submitted_at)}</td>
                  <td className="text-center">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                      {sub.values_count}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setSelectedSub(sub)}
                        className="rounded p-1.5 text-blue-600 hover:bg-blue-50"
                        title="Ver respostas"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteSub(sub)}
                        className="rounded p-1.5 text-red-600 hover:bg-red-50"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>Página {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-40 flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> Anterior
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-40 flex items-center gap-1"
            >
              Próxima <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail slide-over */}
      {selectedSub && (
        <SubmissionDetail submission={selectedSub} onClose={() => setSelectedSub(null)} />
      )}

      {/* Delete modal */}
      {deleteSub && (
        <DeleteModal sub={deleteSub} onClose={() => setDeleteSub(null)} />
      )}
    </div>
  )
}

